import { useContext, useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faLinkedin,
  faFacebook,
  faInstagram,
  faYoutube,
  faTiktok,
  faPinterest,
  faThreads,
  faGoogle,
  faGoogleDrive,
  faBluesky,
  faWordpress,
} from '@fortawesome/free-brands-svg-icons';
import { faPlus, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import channelsApi, { Channel } from '@/services/channels';
import { formatApiError, isPlatformNotAvailableError } from '@/services/api';
import { useChannels } from '@/hooks/useChannels';
import { useEffectiveTeamId } from '@/hooks/useEffectiveTeamId';
import { usePlatformAccess } from '@/hooks/usePlatformAccess';
import { useEntitlement } from '@/hooks/useEntitlement';
import { PackageContext } from '@/contexts/PackageContext';
import { useGatingStore } from '@/stores/gating.store';
import { teamQueryKeys } from '@/lib/queryKeys';
import { useUpdateCanvaLabel } from '@/hooks/useCanvaImport';
import { useReprobeWordPressChannel } from '@/hooks/useWordPress';
import { useUnsavedGuard } from '@/hooks/useUnsavedGuard';
import { useReviewerRestrictions } from '@/hooks/useReviewerRestrictions';
import { WordPressConnectModal } from '@/components/settings/WordPressConnectModal';
import { GDriveTeamPickerModal } from '@/components/gdrive/GDriveTeamPickerModal';
import { GDriveFolderPickerPanel } from '@/components/gdrive/GDriveFolderPickerPanel';
import { GDriveFoldersSection } from '@/components/gdrive/GDriveFoldersSection';
import { getChannelAvatarFromChannel as getChannelProfilePhoto } from '@/utils/channel-avatar';
import { Icon } from '@/components/ui/Icon';
import { Button } from '@/components/ui/Button';
import { Checkbox } from '@/components/ui/Checkbox';
import { RetryImage } from '@/components/ui';
import { ICONS } from '@/constants/icons';
import { useTimezone } from '@/contexts/TimezoneContext';
import { cn } from '@/utils/cn';
import { toast } from '@/utils/toast';
import { ConfirmationModal } from '@/components/ui/ConfirmationModal';
import { Modal } from '@/components/ui/Modal';
import { Tooltip } from '@/components/ui/Tooltip';

/**
 * Detects Meta error code 368 (identity-verification required) in a token-error string.
 * Reconnecting cannot fix this — user must complete identity verification in the FB app.
 */
function isFacebookIdentityVerificationError(lastTokenError: unknown): boolean {
  if (typeof lastTokenError !== 'string') return false;
  return /\bcode:\s*368\b/i.test(lastTokenError);
}

/**
 * Platform configuration with brand colors, gradients, and FontAwesome icons
 */
interface PlatformConfig {
  label: string;
  color: string;
  gradientClass: string;
  faIcon?: IconDefinition;
  customIcon?: string; // Path to custom image icon
  oauthReady: boolean;
  description?: string;
}

const PLATFORM_CONFIG: Record<string, PlatformConfig> = {
  LINKEDIN: {
    label: 'LinkedIn',
    color: '#0A66C2',
    gradientClass: 'bg-gradient-to-r from-[#0A66C2] to-[#0077B5]',
    faIcon: faLinkedin,
    oauthReady: true
  },
  FACEBOOK: {
    label: 'Facebook',
    color: '#1877F2',
    gradientClass: 'bg-gradient-to-r from-[#1877F2] to-[#0866FF]',
    faIcon: faFacebook,
    oauthReady: true,
    description: 'Pages & Instagram'
  },
  INSTAGRAM: {
    label: 'Instagram',
    color: '#E4405F',
    gradientClass: 'bg-gradient-to-r from-[#833AB4] via-[#E4405F] to-[#FCAF45]',
    faIcon: faInstagram,
    oauthReady: true,
    description: 'Business Accounts'
  },
  YOUTUBE: {
    label: 'YouTube',
    color: '#FF0000',
    gradientClass: 'bg-gradient-to-r from-[#FF0000] to-[#CC0000]',
    faIcon: faYoutube,
    oauthReady: true
  },
  TIKTOK: {
    label: 'TikTok',
    color: '#000000',
    gradientClass: 'bg-gradient-to-r from-[#00F2EA] via-black to-[#FF0050]',
    faIcon: faTiktok,
    oauthReady: true
  },
  PINTEREST: {
    label: 'Pinterest',
    color: '#E60023',
    gradientClass: 'bg-gradient-to-r from-[#E60023] to-[#BD081C]',
    faIcon: faPinterest,
    oauthReady: true
  },
  THREADS: {
    label: 'Threads',
    color: '#000000',
    gradientClass: 'bg-gradient-to-r from-gray-700 to-black',
    faIcon: faThreads,
    oauthReady: true
  },
  GOOGLE_MY_BUSINESS: {
    label: 'Google Business',
    color: '#4285F4',
    gradientClass: 'bg-gradient-to-r from-[#4285F4] via-[#EA4335] to-[#34A853]',
    faIcon: faGoogle,
    oauthReady: true
  },
  BLUESKY: {
    label: 'Bluesky',
    color: '#0085FF',
    gradientClass: 'bg-gradient-to-r from-[#0085FF] to-[#0066CC]',
    faIcon: faBluesky,
    oauthReady: true
  },
  CANVA: {
    label: 'Canva',
    color: '#00C4CC',
    gradientClass: 'bg-gradient-to-r from-[#00C4CC] to-[#7D2AE8]',
    customIcon: '/assets/img/canva-circle.png',
    oauthReady: true,
    description: 'Design Import'
  },
  GDRIVE: {
    label: 'Google Drive',
    color: '#4285F4',
    gradientClass: 'bg-gradient-to-r from-[#4285F4] via-[#EA4335] to-[#34A853]',
    faIcon: faGoogleDrive,
    oauthReady: true,
    description: 'Media Import'
  },
  WORDPRESS: {
    label: 'WordPress',
    color: '#21759B',
    gradientClass: 'bg-gradient-to-r from-[#21759B] to-[#1a5c7a]',
    faIcon: faWordpress,
    oauthReady: true,
    description: 'Blog Publishing'
  },
};

// Platform display order (Twitter/X excluded)
const PLATFORM_ORDER = [
  'LINKEDIN', 'FACEBOOK', 'INSTAGRAM', 'YOUTUBE',
  'TIKTOK', 'PINTEREST', 'THREADS', 'GOOGLE_MY_BUSINESS', 'BLUESKY', 'CANVA', 'GDRIVE', 'WORDPRESS'
];

// Constants
const NOTIFICATION_AUTO_HIDE_MS = 5000;
const TOKEN_EXPIRY_SOON_HOURS = 24;
const TOKEN_EXPIRY_WARNING_HOURS = 168;

// Platforms that support automatic token refresh via refresh tokens
// These platforms will auto-refresh when publishing, so "expired" is not a concern
const PLATFORMS_WITH_AUTO_REFRESH = [
  'LINKEDIN',
  'YOUTUBE',
  'GOOGLE_MY_BUSINESS',
  'PINTEREST',
  'TIKTOK',
  'FACEBOOK',
  'INSTAGRAM',
  'THREADS',
  'CANVA',
  'GDRIVE',
  'BLUESKY', // mirrors backend ADAPTERS auto-refresh set
];

interface Notification {
  type: 'success' | 'error';
  message: string;
}

interface TokenStatus {
  text: string;
  dotClass: string;
  bgClass: string;
  textClass: string;
}

/**
 * Calculates the status of a channel's access token based on expiry date
 */
const getTokenStatus = (expiresAt: string | null, t: (key: string) => string, platform?: string): TokenStatus => {
  if (!expiresAt) {
    return {
      text: t('settings_channel_status_active'),
      dotClass: 'bg-green-500',
      bgClass: 'bg-green-50',
      textClass: 'text-green-700'
    };
  }

  const now = new Date();
  const expiry = new Date(expiresAt);
  const hoursUntilExpiry = (expiry.getTime() - now.getTime()) / (1000 * 60 * 60);

  if (hoursUntilExpiry < 0) {
    // Auto-refresh platforms silently refresh their token on the next publish —
    // a red "Expired" pill here causes panic support tickets for nothing.
    if (platform && PLATFORMS_WITH_AUTO_REFRESH.includes(platform)) {
      return {
        text: t('settings_channel_status_autorefresh'),
        dotClass: 'bg-amber-500 animate-pulse',
        bgClass: 'bg-amber-50',
        textClass: 'text-amber-700'
      };
    }
    return {
      text: t('settings_channel_status_expired'),
      dotClass: 'bg-red-500',
      bgClass: 'bg-red-50',
      textClass: 'text-red-700'
    };
  } else if (hoursUntilExpiry < TOKEN_EXPIRY_SOON_HOURS) {
    return {
      text: t('settings_channel_status_expires_soon'),
      dotClass: 'bg-amber-500 animate-pulse',
      bgClass: 'bg-amber-50',
      textClass: 'text-amber-700'
    };
  } else if (hoursUntilExpiry < TOKEN_EXPIRY_WARNING_HOURS) {
    return {
      text: `${Math.floor(hoursUntilExpiry / 24)}d left`,
      dotClass: 'bg-yellow-500',
      bgClass: 'bg-yellow-50',
      textClass: 'text-yellow-700'
    };
  }
  return {
    text: t('settings_channel_status_active'),
    dotClass: 'bg-green-500',
    bgClass: 'bg-green-50',
    textClass: 'text-green-700'
  };
};

/**
 * ChannelSettings Component
 *
 * Modern, elegant channel connection management interface.
 */
export const ChannelSettings = () => {
  const { t } = useTranslation(['settings', 'common']);
  // Platform card sub-labels (PLATFORM_CONFIG keeps English source text; translate at render)
  const platformDescriptions: Record<string, string> = {
    'Pages & Instagram': t('settings_platform_desc_pages_instagram'),
    'Business Accounts': t('settings_platform_desc_business_accounts'),
    'Design Import': t('settings_platform_desc_design_import'),
    'Media Import': t('settings_platform_desc_media_import'),
    'Blog Publishing': t('settings_platform_desc_blog_publishing'),
  };
  const platformDescription = (description: string): string => platformDescriptions[description] ?? description;
  const { formatDateOnly } = useTimezone();
  const queryClient = useQueryClient();
  const { teamId, hasTeam } = useEffectiveTeamId();
  const { isPlatformLocked, upsellPlanSlugFor } = usePlatformAccess();
  // Canva / Google Drive are feature-gated (legacy keys): their connect cards lock like WordPress hides.
  // Existing channels and their disconnect actions stay available.
  const canvaEntitlement = useEntitlement('canva');
  const gdriveEntitlement = useEntitlement('gdrive');
  const wordpressEntitlement = useEntitlement('wordpress');
  // While the package is loading, or failed to load (entitlements null), the lock is undecided:
  // no locked flash and no false lock (the backend still enforces on connect)
  const packageContext = useContext(PackageContext);
  const packageLoading = packageContext?.isLoading === true || packageContext?.error != null;
  const openUpsell = useGatingStore((state) => state.openUpsell);
  // Reviewer-only people may connect and reconnect channels, not disconnect, delete or rename them
  const { canManageChannels } = useReviewerRestrictions();
  const [searchParams, setSearchParams] = useSearchParams();
  const [notification, setNotification] = useState<Notification | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [platformFilter, setPlatformFilter] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');

  // Bulk selection state
  const [selectedChannels, setSelectedChannels] = useState<Set<number>>(new Set());
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const [bulkDeleteModal, setBulkDeleteModal] = useState(false);

  // Modal states
  const [disconnectModal, setDisconnectModal] = useState<{ open: boolean; channel: Channel | null }>({
    open: false,
    channel: null,
  });
  const [renameModal, setRenameModal] = useState<{ open: boolean; channel: Channel | null }>({
    open: false,
    channel: null,
  });
  const [newDisplayName, setNewDisplayName] = useState('');
  const [isRenaming, setIsRenaming] = useState(false);

  // Canva label modal state (shown after canva_connected OAuth callback)
  const [canvaLabelModal, setCanvaLabelModal] = useState<{ open: boolean; channelId: number | null; prefill: string }>({
    open: false,
    channelId: null,
    prefill: '',
  });
  const [canvaLabelValue, setCanvaLabelValue] = useState('');
  const updateCanvaLabel = useUpdateCanvaLabel();

  // Bluesky modal state
  const [blueskyModal, setBlueskyModal] = useState(false);
  const [blueskyHandle, setBlueskyHandle] = useState('');
  const [blueskyAppPassword, setBlueskyAppPassword] = useState('');
  const [isConnectingBluesky, setIsConnectingBluesky] = useState(false);
  const [showBlueskyPassword, setShowBlueskyPassword] = useState(false);

  // WordPress connect modal state
  const [wordpressModal, setWordpressModal] = useState(false);
  const reprobeWordPress = useReprobeWordPressChannel();

  // GDrive team-picker (connect-time team-context fix, SPEC §2/§5.1) and
  // folder-selection step (SPEC §5.1, mandatory before the channel is usable).
  const [gdriveTeamPickerOpen, setGdriveTeamPickerOpen] = useState(false);
  const [isConnectingGDrive, setIsConnectingGDrive] = useState(false);
  const [gdriveFolderPrompt, setGdriveFolderPrompt] = useState<{ open: boolean; channelId: number | null }>({
    open: false,
    channelId: null,
  });

  // Dropdown state (track which channel's dropdown is open)
  const [openDropdown, setOpenDropdown] = useState<number | null>(null);

  // Unsaved-work guard: rename field and Bluesky credential fields are the
  // only free-text inputs on this page; only "dirty" while their modal is open.
  useUnsavedGuard(
    (renameModal.open && newDisplayName.trim().length > 0) ||
      (blueskyModal && (blueskyHandle.trim().length > 0 || blueskyAppPassword.trim().length > 0)),
    'channel-settings'
  );

  // Fetch channels — include inactive so disconnected channels remain visible.
  // Uses the shared useChannels hook so invalidations from anywhere in the app
  // (PostCreator, ChannelSelector, BrandCheck, etc.) are reflected here and vice versa.
  const { data: channels, isLoading, error } = useChannels({ includeInactive: true });

  // Disconnect mutation with proper typing
  const disconnectMutation = useMutation<void, AxiosError<{ error: string }>, number>({
    mutationFn: channelsApi.disconnect,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: teamQueryKeys.channels.all(teamId) });
      setNotification({
        type: 'success',
        message: t('settings_channel_success_disconnect'),
      });
    },
    onError: (err) => {
      setNotification({
        type: 'error',
        message: err.response?.data?.error || t('settings_channel_error_disconnect'),
      });
    },
  });

  // Rename mutation
  const renameMutation = useMutation<Channel, AxiosError<{ error: string }>, { channelId: number; displayName: string }>({
    mutationFn: ({ channelId, displayName }) => channelsApi.updateDisplayName(channelId, displayName),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: teamQueryKeys.channels.all(teamId) });
      setRenameModal({ open: false, channel: null });
      setNewDisplayName('');
      setNotification({ type: 'success', message: t('settings_channel_success_rename') });
    },
    onError: (err) => {
      setNotification({ type: 'error', message: err.response?.data?.error || t('settings_channel_error_rename') });
    },
  });

  // Refresh token mutation for Google platforms (GMB, YouTube)
  const refreshTokenMutation = useMutation<void, AxiosError<{ error: string }>, { channelId: number; platform: string }>({
    mutationFn: async ({ channelId, platform }) => {
      if (platform === 'GOOGLE_MY_BUSINESS') {
        await channelsApi.refreshGoogleMyBusinessToken(channelId);
      }
      // Add YouTube refresh here when needed
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: teamQueryKeys.channels.all(teamId) });
      setOpenDropdown(null);
      setNotification({ type: 'success', message: t('settings_channel_success_refresh_token') });
    },
    onError: (err) => {
      setNotification({ type: 'error', message: err.response?.data?.error || t('settings_channel_error_refresh_token') });
    },
  });

  // Handle OAuth callback notifications
  useEffect(() => {
    const success = searchParams.get('success');
    const errorCode = searchParams.get('error');
    const pagesCount = searchParams.get('pages_count');
    const instagramCount = searchParams.get('instagram_count');
    const platform = searchParams.get('platform');

    if (success) {
      // Use a predicate-based invalidation so the correct team's channel queries are
      // invalidated even when teamId hasn't resolved yet (e.g. God Mode on first render).
      // The predicate matches any query whose key contains the 'channels' segment,
      // covering both teamQueryKeys.channels.all(teamId) → ['team', X, 'channels', ...]
      // and CANVA_KEYS.channels(teamId) → ['canva-channels', X].
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey.includes('channels'),
      });

      if (success === 'gdrive_connected') {
        // Mandatory folder-selection step (SPEC §5.1) — the channel is
        // UNCONFIGURED (fail-closed) until at least one folder is chosen.
        const channelIdParam = searchParams.get('channelId');
        const parsedChannelId = channelIdParam ? parseInt(channelIdParam, 10) : null;
        setSearchParams({});
        if (parsedChannelId !== null && !Number.isNaN(parsedChannelId)) {
          setGdriveFolderPrompt({ open: true, channelId: parsedChannelId });
        } else {
          setNotification({ type: 'success', message: t('gdrive_connected_success', { defaultValue: 'Google Drive connected successfully!' }) });
        }
        return;
      }

      if (success === 'canva_connected') {
        // Open the label-naming dialog; channelId and username come from query params.
        const channelIdParam = searchParams.get('channelId');
        const usernameParam = searchParams.get('username');
        const parsedChannelId = channelIdParam ? parseInt(channelIdParam, 10) : null;
        setSearchParams({});
        if (parsedChannelId !== null && !Number.isNaN(parsedChannelId)) {
          setCanvaLabelValue(usernameParam || '');
          setCanvaLabelModal({ open: true, channelId: parsedChannelId, prefill: usernameParam || '' });
        } else {
          // No valid channelId — just show a generic success notification
          setNotification({ type: 'success', message: t('canva_connected_success', { defaultValue: 'Canva connected successfully!' }) });
        }
        return;
      }

      let message = '';
      if (success === 'linkedin_connected') {
        message = t('settings_channel_oauth_success_platform', { platform: PLATFORM_CONFIG.LINKEDIN.label });
      } else if (success === 'facebook_connected') {
        const details: string[] = [];
        if (pagesCount) details.push(t('common:channels_oauth_success_fb_page_count', { count: parseInt(pagesCount, 10) }));
        if (instagramCount) details.push(t('common:channels_oauth_success_ig_count', { count: parseInt(instagramCount, 10) }));
        const detailText = details.length === 2
          ? t('settings_channel_oauth_list_and', { first: details[0], second: details[1] })
          : details[0];
        message = details.length > 0
          ? t('common:channels_oauth_success_fb_pages', { details: detailText })
          : t('settings_channel_oauth_success_platform', { platform: PLATFORM_CONFIG.FACEBOOK.label });
      } else if (success === 'channel_connected' && platform) {
        const config = PLATFORM_CONFIG[platform.toUpperCase()];
        message = t('settings_channel_oauth_success_platform', { platform: config?.label || platform });
      } else {
        message = t('settings_channel_success_generic');
      }
      setNotification({ type: 'success', message });
      setSearchParams({});
    } else if (errorCode) {
      // Check for custom error message from backend (e.g., limit exceeded messages)
      const customMessage = searchParams.get('message');
      const errorMessages: Record<string, string> = {
        access_denied: t('common:channels_oauth_error_access_denied'),
        invalid_request: t('common:channels_oauth_error_invalid_request'),
        invalid_state: t('common:channels_oauth_error_invalid_state'),
        expired_state: t('common:channels_oauth_error_expired_state'),
        user_not_found: t('common:channels_oauth_error_user_not_found'),
        no_team: t('common:channels_oauth_error_no_team'),
        connection_failed: t('common:channels_oauth_error_connection_failed'),
        no_pages_found: t('common:channels_oauth_error_no_pages_found'),
        token_exchange_failed: t('common:channels_oauth_error_token_exchange_failed'),
        pages_fetch_failed: t('common:channels_oauth_error_pages_fetch_failed'),
      };
      setNotification({
        type: 'error',
        message: customMessage || errorMessages[errorCode] || t('common:channels_oauth_error_generic'),
      });
      setSearchParams({});
    }
  }, [searchParams, queryClient, setSearchParams]);

  // Auto-hide notifications
  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => setNotification(null), NOTIFICATION_AUTO_HIDE_MS);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [notification]);

  // Clear selection when filters change
  useEffect(() => {
    setSelectedChannels(new Set());
  }, [platformFilter, statusFilter, searchQuery]);

  const handleConnect = async (platform: string): Promise<void> => {
    try {
      let authUrl = '';
      switch (platform) {
        case 'LINKEDIN':
          authUrl = await channelsApi.connectLinkedIn();
          break;
        case 'FACEBOOK':
        case 'INSTAGRAM':
          authUrl = await channelsApi.connectFacebook();
          break;
        case 'YOUTUBE':
          authUrl = await channelsApi.connectYouTube();
          break;
        case 'TIKTOK':
          authUrl = await channelsApi.connectTikTok();
          break;
        case 'PINTEREST':
          authUrl = await channelsApi.connectPinterest();
          break;
        case 'THREADS':
          authUrl = await channelsApi.connectThreads();
          break;
        case 'GOOGLE_MY_BUSINESS':
          authUrl = await channelsApi.connectGoogleMyBusiness();
          break;
        case 'BLUESKY':
          // Bluesky uses app password, show modal instead of OAuth redirect
          setBlueskyModal(true);
          return;
        case 'CANVA':
          authUrl = await channelsApi.connectCanva();
          break;
        case 'GDRIVE':
          if (!hasTeam) {
            // No resolvable team (e.g. agency staff with no home team, not in
            // God Mode — CLAUDE.md "Active: User Migration Testing" / SPEC
            // §2). Ask which brand this connection is for before OAuth.
            setGdriveTeamPickerOpen(true);
            return;
          }
          authUrl = await channelsApi.connectGDrive();
          break;
        case 'WORDPRESS':
          // WordPress uses Application Passwords, show modal instead of OAuth redirect
          setWordpressModal(true);
          return;
        default:
          return;
      }
      if (authUrl) {
        window.location.href = authUrl;
      }
    } catch (err) {
      // 403 PLATFORM_NOT_AVAILABLE: the global upsell modal already explains it
      if (isPlatformNotAvailableError(err)) return;
      if (platform === 'GDRIVE') {
        // GDrive routes return either `{ success:false, code, message }`
        // (handleGDriveError in gdrive.routes.ts) or the generic AppError
        // shape `{ success:false, error, code }` (e.g. authorizeTeamAccess's
        // 403 "You do not have access to this team."). formatApiError checks
        // `message` first, falling back to `error`, so both surface correctly
        // instead of the old `data.error`-only read silently dropping the
        // `message`-shaped responses.
        setNotification({ type: 'error', message: formatApiError(err).message });
        return;
      }
      const error = err as AxiosError<{ error: string }>;
      setNotification({
        type: 'error',
        message: error.response?.data?.error || t('settings_channel_oauth_start_failed', { platform: PLATFORM_CONFIG[platform]?.label || platform }),
      });
    }
  };

  /**
   * Continue the GDrive connect flow once the user has picked a team from
   * GDriveTeamPickerModal (SPEC §2/§5.1 team-context fix).
   */
  const handleGDriveTeamPicked = async (pickedTeamId: number): Promise<void> => {
    setIsConnectingGDrive(true);
    try {
      const authUrl = await channelsApi.connectGDrive(pickedTeamId);
      if (authUrl) {
        window.location.href = authUrl;
      }
    } catch (err) {
      setIsConnectingGDrive(false);
      setGdriveTeamPickerOpen(false);
      // See handleConnect's GDRIVE branch — read `message` before falling
      // back to `error` so an authorizeTeamAccess 403 isn't swallowed.
      setNotification({ type: 'error', message: formatApiError(err).message });
    }
  };

  const handleDisconnect = (channel: Channel): void => {
    setDisconnectModal({ open: true, channel });
    setOpenDropdown(null);
  };

  const confirmDisconnect = () => {
    if (disconnectModal.channel) {
      disconnectMutation.mutate(disconnectModal.channel.id);
      setDisconnectModal({ open: false, channel: null });
    }
  };

  const handleRename = (channel: Channel): void => {
    setRenameModal({ open: true, channel });
    // For Canva, pre-fill with the existing label (survives reconnect);
    // for other platforms, use platformUsername as before.
    const prefill = channel.platform === 'CANVA'
      ? (channel.metadata?.label || channel.platformUsername || '')
      : (channel.platformUsername || '');
    setNewDisplayName(prefill);
    setOpenDropdown(null);
  };

  const confirmRename = () => {
    if (!renameModal.channel || !newDisplayName.trim()) return;
    setIsRenaming(true);
    if (renameModal.channel.platform === 'CANVA') {
      // Canva: write to metadata.label via the label endpoint (survives OAuth reconnect)
      updateCanvaLabel.mutate(
        { channelId: renameModal.channel.id, label: newDisplayName.trim() },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: teamQueryKeys.channels.all(teamId) });
            setRenameModal({ open: false, channel: null });
            setNewDisplayName('');
            setNotification({ type: 'success', message: t('settings_channel_success_rename') });
          },
          onError: () => {
            setNotification({ type: 'error', message: t('settings_channel_error_rename') });
          },
          onSettled: () => setIsRenaming(false),
        }
      );
    } else {
      // Non-Canva: write to platformUsername as before
      renameMutation.mutate(
        { channelId: renameModal.channel.id, displayName: newDisplayName.trim() },
        { onSettled: () => setIsRenaming(false) }
      );
    }
  };

  const handleBlueskyConnect = async () => {
    if (!blueskyHandle.trim() || !blueskyAppPassword.trim()) {
      setNotification({ type: 'error', message: t('settings_bluesky_error_required') });
      return;
    }
    setIsConnectingBluesky(true);
    try {
      // Normalize handle: remove @ prefix if user included it
      const normalizedHandle = blueskyHandle.trim().replace(/^@/, '');
      await channelsApi.authenticateBluesky(normalizedHandle, blueskyAppPassword.trim());
      queryClient.invalidateQueries({ queryKey: teamQueryKeys.channels.all(teamId) });
      setBlueskyModal(false);
      setBlueskyHandle('');
      setBlueskyAppPassword('');
      setNotification({ type: 'success', message: t('settings_channel_bluesky_connected') });
    } catch (err) {
      const error = err as AxiosError<{ error: string }>;
      setNotification({
        type: 'error',
        message: error.response?.data?.error || t('settings_channel_bluesky_connect_failed'),
      });
    } finally {
      setIsConnectingBluesky(false);
    }
  };

  // Predicate: does this channel match the current statusFilter?
  const matchesStatus = (c: Channel): boolean => {
    if (statusFilter === 'active') return c.isActive === true;
    if (statusFilter === 'inactive') return c.isActive === false;
    return true; // 'all'
  };

  const getConnectedCount = (platform: string): number => {
    return channels?.filter(c => c.platform === platform && matchesStatus(c)).length || 0;
  };

  // Get platforms that have channels matching the current statusFilter, with their counts.
  // Platforms with zero matching channels are hidden so the pill row stays tight.
  const getAvailablePlatforms = (): { platform: string; count: number }[] => {
    if (!channels) return [];
    const platformCounts = channels.reduce((acc, channel) => {
      if (!matchesStatus(channel)) return acc;
      acc[channel.platform] = (acc[channel.platform] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    return PLATFORM_ORDER
      .filter(platform => platformCounts[platform] > 0)
      .map(platform => ({ platform, count: platformCounts[platform] }));
  };

  // Auto-clear platformFilter when switching statusFilter causes the selected
  // platform to disappear from the available platforms list.
  useEffect(() => {
    if (platformFilter === null) return;
    const available = getAvailablePlatforms();
    const stillAvailable = available.some(({ platform }) => platform === platformFilter);
    if (!stillAvailable) {
      setPlatformFilter(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, channels]);

  // Derived counts for inactive banner and status pills
  const inactiveCount = channels?.filter(c => !c.isActive).length ?? 0;

  // Filter channels based on search query, platform filter, and status filter
  const filteredChannels = channels?.filter(channel => {
    if (platformFilter && channel.platform !== platformFilter) {
      return false;
    }
    if (statusFilter === 'active' && !channel.isActive) {
      return false;
    }
    if (statusFilter === 'inactive' && channel.isActive) {
      return false;
    }
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const username = (channel.platformUsername || '').toLowerCase();
      const platformLabel = (PLATFORM_CONFIG[channel.platform]?.label || '').toLowerCase();
      return username.includes(query) || platformLabel.includes(query);
    }
    return true;
  }) || [];

  // Bulk selection helpers
  const toggleChannelSelection = (channelId: number) => {
    setSelectedChannels(prev => {
      const next = new Set(prev);
      if (next.has(channelId)) {
        next.delete(channelId);
      } else {
        next.add(channelId);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (!filteredChannels) return;
    const allIds = filteredChannels.map(c => c.id);
    const allSelected = allIds.every(id => selectedChannels.has(id));

    if (allSelected) {
      setSelectedChannels(new Set());
    } else {
      setSelectedChannels(new Set(allIds));
    }
  };

  const isAllSelected = filteredChannels && filteredChannels.length > 0 &&
    filteredChannels.every(c => selectedChannels.has(c.id));

  const handleBulkDeleteClick = () => {
    if (!canManageChannels || selectedChannels.size === 0) return;
    setBulkDeleteModal(true);
  };

  const confirmBulkDelete = async () => {
    if (!canManageChannels || selectedChannels.size === 0) return;

    setIsBulkDeleting(true);
    try {
      // Delete channels one by one using the disconnect API
      const deletePromises = Array.from(selectedChannels).map(id =>
        channelsApi.disconnect(id)
      );
      await Promise.all(deletePromises);

      toast.success(t('settings_channel_bulk_delete_success', { count: selectedChannels.size }));
      setSelectedChannels(new Set());
      setBulkDeleteModal(false);
      queryClient.invalidateQueries({ queryKey: teamQueryKeys.channels.all(teamId) });
    } catch (error) {
      toast.error(t('settings_channel_bulk_delete_error'));
    } finally {
      setIsBulkDeleting(false);
    }
  };

  return (
    <div className="space-y-6 sm:space-y-8">
      {/* Fixed Toast Notification */}
      {notification && (
        <div className={cn(
          'fixed bottom-[calc(var(--bottom-nav-h)+env(safe-area-inset-bottom)+12px+var(--floating-stack-offset))] lg:bottom-6 right-6 z-50 flex items-center gap-3 rounded-xl px-5 py-4 shadow-2xl',
          'animate-in slide-in-from-bottom-5 duration-300',
          notification.type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
        )}>
          <Icon
            name={notification.type === 'success' ? ICONS.CHECK_CIRCLE : ICONS.CROSS_CIRCLE}
            size="sm"
            color="text-white"
            ariaHidden
          />
          <p className="font-medium">{notification.message}</p>
          <button
            onClick={() => setNotification(null)}
            className="ml-2 hover:opacity-80 transition-opacity"
            aria-label={t('settings_channel_dismiss_notification')}
          >
            <Icon name={ICONS.CROSS} size="sm" color="text-white/80" ariaHidden />
          </button>
        </div>
      )}

      {/* Section Header */}
      <div className="text-center sm:text-left">
        <h2 className="text-lg sm:text-2xl font-bold text-gray-900">{t('settings_channel_header_title')}</h2>
        <p className="mt-2 text-sm sm:text-base text-gray-600">
          {t('settings_channel_header_description')}
        </p>
      </div>

      {/* Platform Cards Grid */}
      <div className="grid grid-cols-2 max-w-xs mx-auto gap-3 sm:max-w-none sm:mx-0 sm:flex sm:flex-wrap sm:gap-2.5">
        {PLATFORM_ORDER.map((platform) => {
          const config = PLATFORM_CONFIG[platform];
          if (!config) return null;
          // WordPress / Canva / Drive connect cards stay visible when the plan lacks them: locked, click opens
          // the upsell. Already-connected channels still show in the connected-channels list below.
          const connectedCount = getConnectedCount(platform);
          // Package does not include this platform: no NEW connections (existing channels are kept for the team)
          const featureEntitlement =
            platform === 'CANVA'
              ? canvaEntitlement
              : platform === 'GDRIVE'
                ? gdriveEntitlement
                : platform === 'WORDPRESS'
                  ? wordpressEntitlement
                  : null;
          const featureKey = platform === 'CANVA' ? 'canva' : platform === 'GDRIVE' ? 'gdrive' : 'wordpress';
          const featureKeyLocked = !packageLoading && featureEntitlement?.locked === true;
          const platformLocked = isPlatformLocked(platform) || featureKeyLocked;
          const hasGrandfathered = platformLocked && (channels?.some((c) => c.platform === platform) ?? false);

          const tooltipText = platformLocked
            ? t('settings_channel_platform_locked_tooltip', { platform: config.label })
            : !config.oauthReady
            ? t('settings_channel_button_coming_soon')
            : platform === 'INSTAGRAM'
              ? t('settings_channel_button_connect_via_facebook')
              : connectedCount > 0
                ? t('settings_channel_button_add_another')
                : t('settings_channel_button_connect');

          return (
            <div
              key={platform}
              {...(featureKeyLocked ? { 'data-testid': `locked-entry-${featureKey}` } : {})}
              className="group relative flex flex-col items-center justify-center overflow-hidden rounded-xl border border-gray-200 bg-white p-3 transition-all duration-300 hover:border-gray-300 hover:shadow-lg aspect-square sm:aspect-auto sm:w-36 sm:h-36"
            >
              {/* Gradient accent bar */}
              <div className={cn('absolute inset-x-0 top-0 h-1', config.gradientClass)} />

              {/* Connect/Coming soon button - top-right corner */}
              <Tooltip content={tooltipText}>
                <button
                  type="button"
                  onClick={() =>
                    featureKeyLocked
                      ? openUpsell(
                          {
                            feature: featureKey,
                            upsellPlanSlug: featureEntitlement?.upsellPlanSlug ?? null,
                          },
                          true,
                        )
                      : platformLocked
                      ? openUpsell({ feature: `platform:${platform}`, variant: 'platform', platform, upsellPlanSlug: upsellPlanSlugFor(platform) }, true)
                      : handleConnect(platform)
                  }
                  disabled={!config.oauthReady}
                  aria-label={tooltipText}
                  data-testid={`connect-platform-${platform}`}
                  data-locked={platformLocked ? 'true' : undefined}
                  className={cn(
                    'absolute top-2 right-2 w-7 h-7 flex items-center justify-center rounded-full transition-all duration-200',
                    config.oauthReady
                      ? 'text-gray-400 hover:text-gray-700 hover:bg-gray-100 hover:scale-110 active:scale-95'
                      : 'text-gray-300 cursor-not-allowed'
                  )}
                >
                  {platformLocked ? (
                    <Icon name={ICONS.LOCK} size="xs" ariaHidden />
                  ) : (
                    <FontAwesomeIcon icon={faPlus} className="text-xs" aria-hidden="true" />
                  )}
                </button>
              </Tooltip>

              {/* Platform icon */}
              <div className="mb-1.5 transition-transform duration-300 group-hover:scale-110">
                {config.customIcon ? (
                  <RetryImage
                    src={config.customIcon}
                    alt={config.label}
                    className="h-6 w-6 sm:h-8 sm:w-8 object-contain"
                  />
                ) : config.faIcon ? (
                  <FontAwesomeIcon
                    icon={config.faIcon}
                    className="text-2xl sm:text-3xl"
                    style={{ color: config.color }}
                    aria-hidden="true"
                  />
                ) : null}
              </div>

              {/* Platform name */}
              <h3 className="text-center text-sm font-semibold text-gray-900">{config.label}</h3>

              {/* Description */}
              {config.description && (
                <p className="mt-0.5 text-center text-xs text-gray-500">{platformDescription(config.description)}</p>
              )}

              {/* Locked: not in the package. Existing channels stay (grandfathered). */}
              {platformLocked && (
                <p className="mt-1.5 flex items-center justify-center gap-1 text-center text-[11px] font-medium leading-tight text-primary-700">
                  <Icon name={ICONS.LOCK} size="xs" ariaHidden />
                  <span>
                    {hasGrandfathered
                      ? t('settings_channel_platform_not_in_plan_kept')
                      : t('settings_channel_platform_not_in_plan')}
                  </span>
                </p>
              )}

              {/* Connected badge */}
              {connectedCount > 0 && (
                <div className="mt-1.5 flex items-center justify-center gap-1">
                  <span className="h-1.5 w-1.5 rounded-full bg-green-500 animate-pulse" />
                  <span className="text-xs font-medium text-green-600">
                    {connectedCount} {t('settings_channel_badge_connected')}
                  </span>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Info Box */}
      <div className="flex items-start gap-3 rounded-xl border border-light-teal-gray bg-soft-mint p-4">
        <Icon name={ICONS.INFO} size="sm" color="text-dark-teal" ariaHidden />
        <p className="text-sm text-dark-teal">
          <strong>{t('settings_channel_info_note')}</strong> {t('settings_channel_info_instagram')}
        </p>
      </div>

      {/* Inactive Channels Banner — only rendered when at least one channel needs reconnection */}
      {inactiveCount > 0 && (
        <div className="flex items-center justify-between gap-4 rounded-xl border border-amber-200 bg-amber-50 pl-4 pr-5 py-4 border-l-4 border-l-amber-400">
          <div className="flex items-start gap-3 min-w-0">
            <Icon name={ICONS.WARNING} size="sm" color="text-amber-600" ariaHidden className="mt-0.5 flex-shrink-0" />
            <div className="min-w-0">
              <p className="text-sm font-medium text-amber-900">
                {t('channels_inactive_banner_message', { count: inactiveCount })}
              </p>
              {channels && (() => {
                const inactiveChannels = channels.filter(c => !c.isActive);
                const getChannelLabel = (c: Channel): string => {
                  const username = c.platformUsername;
                  if (username && username.toLowerCase() !== '[deleted]') return username;
                  return PLATFORM_CONFIG[c.platform]?.label || c.platform;
                };
                const shown = inactiveChannels.slice(0, 2).map(getChannelLabel);
                const remainder = inactiveChannels.length - shown.length;
                return (
                  <p className="mt-0.5 text-xs text-amber-700">
                    {shown.join(', ')}
                    {remainder > 0 && <span className="ml-1">+{remainder} more</span>}
                  </p>
                );
              })()}
            </div>
          </div>
          <Button
            variant="primary"
            size="sm"
            className="flex-shrink-0"
            onClick={() => {
              setStatusFilter('inactive');
              setPlatformFilter(null);
            }}
          >
            {t('channels_inactive_banner_action')}
            <FontAwesomeIcon icon={faChevronRight} className="ml-1 text-xs" aria-hidden="true" />
          </Button>
        </div>
      )}

      {/* Connected Channels Section */}
      <div className="rounded-2xl border border-gray-200 bg-white shadow-sm">
        <div className="border-b border-gray-100 bg-gray-50/50 px-6 py-4 space-y-4">
          {/* Title Row */}
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-semibold text-gray-900">{t('settings_channel_section_title')}</h2>
            {channels && channels.length > 0 && (
              <span className="rounded-full bg-primary-100 px-2.5 py-0.5 text-sm font-semibold text-primary-700">
                {channels.length}
              </span>
            )}
          </div>

          {/* Search and Filters - only show if there are channels */}
          {channels && channels.length > 0 && (
            <div className="space-y-3">
              {/* Search Input - full width row */}
              <div className="relative w-full max-w-md">
                <Icon
                  name={ICONS.SEARCH}
                  size="sm"
                  color="text-gray-400"
                  ariaHidden
                  className="absolute left-3 top-1/2 -translate-y-1/2"
                />
                <input
                  type="text"
                  placeholder={t('settings_channel_search_placeholder')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full h-10 pl-10 pr-10 rounded-lg border border-gray-200 bg-white text-sm placeholder-gray-400 focus:border-gray-400 outline-none transition-colors"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    aria-label={t('settings_channel_clear_search')}
                  >
                    <Icon name={ICONS.CROSS} size="sm" ariaHidden />
                  </button>
                )}
              </div>

              {/* Status Filter Pills - above platform pills; smaller/less prominent */}
              <div className="flex flex-wrap gap-1.5">
                {(['all', 'active', 'inactive'] as const).map((status) => {
                  if (status === 'inactive' && inactiveCount === 0) return null;
                  const isSelected = statusFilter === status;
                  const label = status === 'all'
                    ? t('channels_status_filter_all')
                    : status === 'active'
                      ? t('channels_status_filter_active')
                      : t('channels_status_filter_inactive');
                  return (
                    <button
                      key={status}
                      onClick={() => setStatusFilter(status)}
                      className={cn(
                        'px-2.5 py-0.5 rounded-full text-xs font-medium transition-colors',
                        isSelected
                          ? 'bg-gray-700 text-white'
                          : 'border border-gray-200 text-gray-500 hover:bg-gray-50 hover:border-gray-300'
                      )}
                    >
                      {label}
                      {status === 'inactive' && (
                        <span className={cn('ml-1', isSelected ? 'text-white/70' : 'text-gray-400')}>
                          ({inactiveCount})
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Platform Filter Pills - separate row */}
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setPlatformFilter(null)}
                  className={cn(
                    'px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-full text-xs sm:text-sm font-medium transition-colors',
                    platformFilter === null
                      ? 'bg-gray-900 text-white'
                      : 'border border-gray-300 text-gray-600 hover:bg-gray-50'
                  )}
                >
                  {t('settings_channel_filter_all')}
                </button>
                {getAvailablePlatforms().map(({ platform, count }) => {
                  const config = PLATFORM_CONFIG[platform];
                  const isActive = platformFilter === platform;
                  return (
                    <button
                      key={platform}
                      onClick={() => setPlatformFilter(isActive ? null : platform)}
                      className={cn(
                        'flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-colors',
                        isActive
                          ? 'text-white'
                          : 'border border-gray-300 hover:bg-gray-50'
                      )}
                      style={isActive ? { backgroundColor: config?.color } : undefined}
                    >
                      {config?.faIcon && (
                        <FontAwesomeIcon
                          icon={config.faIcon}
                          className="text-xs"
                          style={{ color: isActive ? 'white' : config?.color }}
                          aria-hidden="true"
                        />
                      )}
                      <span style={{ color: isActive ? 'white' : config?.color }}>{config?.label}</span>
                      <span
                        className={cn(
                          'ml-0.5 text-xs',
                          isActive ? 'text-white/80' : 'text-gray-400'
                        )}
                      >
                        ({count})
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Bulk Action Bar - shows when channels are selected */}
          {canManageChannels && selectedChannels.size > 0 && (
            <div className="flex items-center justify-between bg-primary-50 border border-primary-200 rounded-lg px-4 py-2">
              <span className="text-sm font-medium text-primary-700">
                {t('settings_channel_selected', { count: selectedChannels.size })}
              </span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setSelectedChannels(new Set())}
                  className="text-sm text-gray-600 hover:text-gray-800"
                >
                  {t('settings_channel_clear_selection')}
                </button>
                <Button
                  variant="danger"
                  size="sm"
                  onClick={handleBulkDeleteClick}
                  disabled={isBulkDeleting}
                >
                  <Icon name={ICONS.TRASH} size="sm" ariaHidden className="mr-1" />
                  {t('settings_channel_delete_selected')}
                </Button>
              </div>
            </div>
          )}
        </div>

        {isLoading ? (
          <div className="py-16 text-center">
            <div className="mx-auto h-10 w-10 animate-spin rounded-full border-4 border-gray-200 border-t-primary-600" />
            <p className="mt-4 font-medium text-gray-600">{t('settings_channel_loading')}</p>
          </div>
        ) : error ? (
          <div className="py-16 text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-red-50">
              <Icon name={ICONS.WARNING} size="lg" color="text-red-500" ariaHidden />
            </div>
            <p className="font-medium text-red-600">{t('settings_channel_error_load')}</p>
            <p className="mt-1 text-sm text-gray-500">{t('settings_channel_refresh_notice')}</p>
          </div>
        ) : channels && channels.length > 0 ? (
          filteredChannels.length > 0 ? (
            <div className="divide-y divide-gray-100">
              {/* Table Header with Select All */}
              <div className="flex items-center justify-between px-3 sm:px-6 py-3 bg-gray-50/80 border-b border-gray-200">
                <div className="flex items-center gap-4">
                  {canManageChannels && (
                    <>
                      <div className="flex-shrink-0">
                        <Checkbox
                          checked={isAllSelected}
                          onCheckedChange={toggleSelectAll}
                        />
                      </div>
                      <span className="text-sm font-medium text-gray-600">
                        {isAllSelected ? t('settings_channel_deselect_all') : t('settings_channel_select_all')}
                      </span>
                    </>
                  )}
                </div>
                <span className="text-sm text-gray-500">
                  {t('settings_channel_count', { count: filteredChannels.length })}
                </span>
              </div>
              {filteredChannels.map((channel) => {
                const config = PLATFORM_CONFIG[channel.platform];
                const tokenStatus = getTokenStatus(channel.tokenExpiresAt, t, channel.platform);
                const isInactive = channel.isActive === false;
                const needsIdentityVerification =
                  channel.platform === 'FACEBOOK' &&
                  isFacebookIdentityVerificationError(channel.metadata?.lastTokenError);
                const reconnectTooltip = needsIdentityVerification
                  ? t('channels_identity_verification_tooltip')
                  : channel.metadata?.lastTokenError
                    ? String(channel.metadata.lastTokenError)
                    : t('channels_reconnect_tooltip_fallback');

                return (
                  <div key={channel.id}>
                  <div
                    className="flex items-center justify-between px-3 sm:px-6 py-3 sm:py-4 transition-colors hover:bg-gray-50/50"
                  >
                    {/* Left side + token pill — dimmed when inactive so the dropdown stays fully opaque */}
                    <div className={cn(
                      'flex items-center gap-2 sm:gap-4 min-w-0',
                      isInactive && 'opacity-60'
                    )}>
                      {/* Selection Checkbox (bulk delete only) */}
                      {canManageChannels && (
                        <div className="flex-shrink-0">
                          <Checkbox
                            checked={selectedChannels.has(channel.id)}
                            onCheckedChange={() => toggleChannelSelection(channel.id)}
                          />
                        </div>
                      )}

                      <div className={cn(
                        'flex-shrink-0 flex h-9 w-9 sm:h-12 sm:w-12 items-center justify-center',
                        isInactive && 'grayscale'
                      )}>
                        {getChannelProfilePhoto(channel) ? (
                          <RetryImage
                            src={getChannelProfilePhoto(channel)!}
                            alt={channel.platformUsername || config?.label || ''}
                            className="h-8 w-8 sm:h-10 sm:w-10 rounded-full object-cover"
                          />
                        ) : config?.customIcon ? (
                          <RetryImage
                            src={config.customIcon}
                            alt={config.label}
                            className="h-8 w-8 sm:h-10 sm:w-10 object-contain"
                          />
                        ) : config?.faIcon ? (
                          <FontAwesomeIcon
                            icon={config.faIcon}
                            className="text-2xl sm:text-3xl"
                            style={{ color: config.color }}
                            aria-hidden="true"
                          />
                        ) : (
                          <Icon name={ICONS.GLOBE} size="lg" color="text-gray-500" ariaHidden />
                        )}
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-sm sm:text-base font-semibold text-gray-900 truncate">
                            {channel.platform === 'CANVA'
                              ? (channel.metadata?.label || channel.platformUsername || config?.label || channel.platform)
                              : channel.platform === 'WORDPRESS'
                                ? (typeof channel.metadata?.siteUrl === 'string'
                                    ? channel.metadata.siteUrl.replace(/^https?:\/\//, '')
                                    : channel.platformUsername || config?.label || channel.platform)
                                : (channel.platformUsername || config?.label || channel.platform)}
                          </p>
                          {/* Platform not in the package: the channel is grandfathered (keeps publishing + reconnect) */}
                          {isPlatformLocked(channel.platform) && (
                            <span
                              data-testid="channel-grandfathered-badge"
                              className="inline-flex items-center gap-1 rounded-full bg-primary-50 px-2 py-0.5 text-xs font-medium text-primary-700"
                            >
                              <Icon name={ICONS.LOCK} size="xs" ariaHidden />
                              {t('settings_channel_platform_not_in_plan_kept')}
                            </span>
                          )}
                          {/* Inactive badge — only shown when channel is disconnected */}
                          {isInactive && (
                            <Tooltip content={reconnectTooltip} side="top">
                              <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700 cursor-help">
                                <Icon name={ICONS.WARNING} size="xs" ariaHidden />
                                {needsIdentityVerification
                                  ? t('channels_identity_verification_required')
                                  : t('channels_reconnect_required')}
                              </span>
                            </Tooltip>
                          )}
                        </div>
                        <p className="text-xs sm:text-sm text-gray-500">{config?.label || channel.platform}</p>
                        {/* Discriminator hint for Canva channels without a user-set label */}
                        {channel.platform === 'CANVA' && !channel.metadata?.label && (() => {
                          const teamSlug = channel.metadata?.canvaTeamId;
                          const userSlug = channel.metadata?.canvaUserId;
                          if (teamSlug && typeof teamSlug === 'string' && teamSlug.length > 0) {
                            return (
                              <p className="text-xs text-gray-400 font-mono">
                                {'Team '}{teamSlug.slice(0, 8)}{teamSlug.length > 8 ? '…' : ''}
                              </p>
                            );
                          }
                          if (userSlug && typeof userSlug === 'string' && userSlug.length > 0) {
                            return (
                              <p className="text-xs text-gray-400 font-mono">
                                {'User '}{userSlug.slice(0, 8)}{userSlug.length > 8 ? '…' : ''}
                              </p>
                            );
                          }
                          return null;
                        })()}
                        {/* WordPress username hint (site URL is already the primary label above) */}
                        {channel.platform === 'WORDPRESS' && channel.platformUsername && (
                          <p className="text-xs text-gray-400 truncate">
                            {t('wordpress_channel_username_hint', { defaultValue: `as ${channel.platformUsername}`, username: channel.platformUsername })}
                          </p>
                        )}
                        <p className="mt-0.5 text-xs text-gray-400 truncate">
                          {t('settings_channel_connected_date', { date: formatDateOnly(channel.createdAt) })}
                          {/* Show reconnected date only when the backend recorded a genuine OAuth reconnect */}
                          {(() => {
                            const lastReconnectedAt: unknown = channel.metadata?.lastReconnectedAt;
                            if (typeof lastReconnectedAt !== 'string' || lastReconnectedAt.length === 0) {
                              return null;
                            }
                            return (
                              <span className="ml-1">
                                · {t('settings_channel_reconnected_date', { date: formatDateOnly(lastReconnectedAt) })}
                              </span>
                            );
                          })()}
                          {channel.connectedBy && (
                            <span className="ml-1">
                              {t('settings_channel_connected_by', { name: channel.connectedBy.fullName })}
                            </span>
                          )}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 sm:gap-4 flex-shrink-0">
                      {/* Inactive channel: show Reconnect button instead of token status pill */}
                      <div className={cn(isInactive && 'opacity-60')}>
                        {isInactive ? (
                          <Tooltip content={reconnectTooltip} side="top">
                            {/* Single wrapper so Radix Trigger receives exactly one element.
                                Responsive visibility is preserved by the buttons' own classes. */}
                            <span className="inline-flex">
                              {/* Desktop pill — hidden below sm */}
                              <button
                                onClick={needsIdentityVerification
                                  ? () => window.open('https://www.facebook.com/help/contact/367438723733904', '_blank', 'noopener,noreferrer')
                                  : () => handleConnect(channel.platform)}
                                className="hidden sm:flex items-center gap-1.5 rounded-full bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700 transition-colors"
                              >
                                <Icon name={needsIdentityVerification ? ICONS.EXTERNAL_LINK : ICONS.REFRESH} size="xs" ariaHidden />
                                {needsIdentityVerification
                                  ? t('channels_identity_verification_action')
                                  : t('channels_reconnect_action')}
                              </button>
                              {/* Mobile pill — visible only below sm */}
                              <button
                                onClick={needsIdentityVerification
                                  ? () => window.open('https://www.facebook.com/help/contact/367438723733904', '_blank', 'noopener,noreferrer')
                                  : () => handleConnect(channel.platform)}
                                className="flex sm:hidden items-center gap-1 rounded-full bg-red-600 px-2.5 py-1.5 text-xs font-medium text-white hover:bg-red-700 transition-colors"
                              >
                                <Icon name={needsIdentityVerification ? ICONS.EXTERNAL_LINK : ICONS.REFRESH} size="xs" ariaHidden />
                                {needsIdentityVerification
                                  ? t('channels_identity_verification_action')
                                  : t('channels_reconnect_action')}
                              </button>
                            </span>
                          </Tooltip>
                        ) : (
                          <div className={cn('hidden sm:flex items-center gap-2 rounded-full px-3 py-1.5', tokenStatus.bgClass)}>
                            <span className={cn('h-2 w-2 rounded-full', tokenStatus.dotClass)} />
                            <span className={cn('text-sm font-medium', tokenStatus.textClass)}>
                              {tokenStatus.text}
                            </span>
                            {/* Info tooltip for expired/expiring/auto-refreshed tokens on platforms with auto-refresh */}
                            {(tokenStatus.text === t('settings_channel_status_expired') ||
                              tokenStatus.text === t('settings_channel_status_expires_soon') ||
                              tokenStatus.text === t('settings_channel_status_autorefresh')) &&
                              PLATFORMS_WITH_AUTO_REFRESH.includes(channel.platform) && (
                              <Tooltip
                                content={t('settings_channel_expired_tooltip')}
                                side="top"
                              >
                                <button
                                  type="button"
                                  className={cn(
                                    'ml-0.5 cursor-help',
                                    tokenStatus.text === t('settings_channel_status_expired')
                                      ? 'text-red-400 hover:text-red-600'
                                      : 'text-amber-400 hover:text-amber-600'
                                  )}
                                  aria-label={t('settings_channel_expired_tooltip')}
                                >
                                  <Icon name={ICONS.INFO} size="xs" ariaHidden />
                                </button>
                              </Tooltip>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Actions Dropdown — hidden for inactive rows; Reconnect button is sufficient */}
                      {!isInactive && <div className="relative">
                        <button
                          onClick={() => setOpenDropdown(openDropdown === channel.id ? null : channel.id)}
                          className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                          aria-label={t('settings_channel_actions')}
                        >
                          <Icon name={ICONS.ELLIPSIS_VERTICAL} size="sm" ariaHidden />
                        </button>

                        {openDropdown === channel.id && (
                          <>
                            {/* Backdrop to close dropdown */}
                            <div
                              className="fixed inset-0 z-40"
                              onClick={() => setOpenDropdown(null)}
                            />

                            {/* Dropdown Menu */}
                            <div className="absolute right-0 top-full mt-1 z-50 w-max min-w-[12rem] max-w-[18rem] rounded-xl bg-white border border-gray-200 shadow-lg overflow-hidden">
                              {/* Reconnect — shown for inactive channels */}
                              {isInactive && (
                                <button
                                  onClick={() => {
                                    setOpenDropdown(null);
                                    if (needsIdentityVerification) {
                                      window.open('https://www.facebook.com/help/contact/367438723733904', '_blank', 'noopener,noreferrer');
                                    } else {
                                      void handleConnect(channel.platform);
                                    }
                                  }}
                                  className="w-full flex items-center gap-3 px-4 py-3 text-sm text-red-600 hover:bg-red-50 transition-colors whitespace-nowrap"
                                >
                                  <Icon name={needsIdentityVerification ? ICONS.EXTERNAL_LINK : ICONS.REFRESH} size="sm" color="text-red-400" ariaHidden />
                                  {needsIdentityVerification
                                    ? t('channels_identity_verification_action')
                                    : t('channels_reconnect_action')}
                                </button>
                              )}
                              <Tooltip content={t('settings_channel_manage_reviewer_hint')} disabled={canManageChannels}>
                                <div className="w-full">
                                  <button
                                    onClick={() => handleRename(channel)}
                                    disabled={!canManageChannels}
                                    className="w-full flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors whitespace-nowrap disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent"
                                  >
                                    <Icon name={ICONS.EDIT} size="sm" color="text-gray-400" ariaHidden />
                                    {t('settings_modal_rename_title')}
                                  </button>
                                </div>
                              </Tooltip>
                              {/* Refresh Token - only for Google platforms with refresh tokens */}
                              {channel.platform === 'GOOGLE_MY_BUSINESS' && !isInactive && (
                                <button
                                  onClick={() => refreshTokenMutation.mutate({ channelId: channel.id, platform: channel.platform })}
                                  disabled={refreshTokenMutation.isPending}
                                  className="w-full flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50 whitespace-nowrap"
                                >
                                  <Icon name={ICONS.REFRESH} size="sm" color="text-gray-400" ariaHidden />
                                  {refreshTokenMutation.isPending ? t('settings_channel_refreshing_token') : t('settings_channel_refresh_token')}
                                </button>
                              )}
                              {/* Re-check connection - WordPress health probe (CLAUDE.md rule 15:
                                  never trust a stale "connected" state at face value) */}
                              {channel.platform === 'WORDPRESS' && !isInactive && (
                                <button
                                  onClick={() => {
                                    setOpenDropdown(null);
                                    reprobeWordPress.mutate(channel.id, {
                                      onSuccess: ({ health }) => {
                                        setNotification({
                                          type: health.status === 'healthy' ? 'success' : 'error',
                                          message: health.message,
                                        });
                                      },
                                      onError: (err) => {
                                        setNotification({
                                          type: 'error',
                                          message: formatApiError(err).message
                                            || t('wordpress_reprobe_error', { defaultValue: 'Failed to re-check connection' }),
                                        });
                                      },
                                    });
                                  }}
                                  disabled={reprobeWordPress.isPending}
                                  className="w-full flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50 whitespace-nowrap"
                                >
                                  <Icon name={ICONS.REFRESH} size="sm" color="text-gray-400" ariaHidden />
                                  {reprobeWordPress.isPending
                                    ? t('wordpress_reprobe_checking', { defaultValue: 'Checking...' })
                                    : t('wordpress_reprobe_action', { defaultValue: 'Re-check Connection' })}
                                </button>
                              )}
                              <Tooltip content={t('settings_channel_manage_reviewer_hint')} disabled={canManageChannels}>
                                <div className="w-full">
                                  <button
                                    onClick={() => handleDisconnect(channel)}
                                    disabled={!canManageChannels}
                                    className="w-full flex items-center gap-3 px-4 py-3 text-sm text-red-600 hover:bg-red-50 transition-colors whitespace-nowrap disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent"
                                  >
                                    <Icon name={ICONS.CROSS_CIRCLE} size="sm" color="text-red-400" ariaHidden />
                                    {t('settings_modal_disconnect_confirm')}
                                  </button>
                                </div>
                              </Tooltip>
                            </div>
                          </>
                        )}
                      </div>}
                    </div>
                  </div>
                  {/* Shared folders manager — GDrive only (SPEC §5.3, §7) */}
                  {channel.platform === 'GDRIVE' && !isInactive && (
                    <div className="px-3 sm:px-6 pb-3 -mt-1">
                      <p className="mb-2 text-xs text-gray-400">
                        {t('settings_gdrive_per_person_hint', {
                          defaultValue:
                            "Google Drive connects per person. Unlike other channels it can't be shared across the team — each member connects their own account.",
                        })}
                      </p>
                      <GDriveFoldersSection channelId={channel.id} />
                    </div>
                  )}
                  {/* GDrive folder-selection step — shown inline, directly under
                      this row, immediately after a fresh connect (SPEC §5.1).
                      Deliberately NOT a modal/overlay — see
                      GDriveFolderPickerPanel.tsx's positioning-bug postmortem. */}
                  {channel.platform === 'GDRIVE' &&
                    gdriveFolderPrompt.open &&
                    gdriveFolderPrompt.channelId === channel.id && (
                      <div className="px-3 sm:px-6 pb-3 -mt-1">
                        <GDriveFolderPickerPanel
                          open={gdriveFolderPrompt.open}
                          onOpenChange={(open) =>
                            setGdriveFolderPrompt((prev) => ({ ...prev, open }))
                          }
                          channelId={channel.id}
                          onSaved={() => {
                            setNotification({
                              type: 'success',
                              message: t('gdrive_connected_success', { defaultValue: 'Google Drive connected successfully!' }),
                            });
                          }}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="py-16 text-center">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-gray-100">
                <Icon name={ICONS.SEARCH} size="lg" color="text-gray-400" ariaHidden />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">{t('settings_channel_no_results')}</h3>
              <p className="mx-auto mt-2 max-w-sm text-gray-600">
                {searchQuery
                  ? t('settings_channel_empty_search', { query: searchQuery })
                  : statusFilter === 'inactive'
                    ? t('settings_channel_empty_inactive')
                    : t('settings_channel_empty_platform', { platform: PLATFORM_CONFIG[platformFilter!]?.label || platformFilter })}
              </p>
              <button
                onClick={() => {
                  setSearchQuery('');
                  setPlatformFilter(null);
                  setStatusFilter('all');
                }}
                className="mt-4 text-sm font-medium text-primary-600 hover:text-primary-700"
              >
                {t('settings_channel_clear_filters')}
              </button>
            </div>
          )
        ) : (
          <div className="py-16 text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-gray-100">
              <Icon name={ICONS.LINK} size="lg" color="text-gray-400" ariaHidden />
            </div>
            <h3 className="text-lg font-semibold text-gray-900">{t('settings_channel_empty_title')}</h3>
            <p className="mx-auto mt-2 max-w-sm text-gray-600">
              {t('settings_channel_empty_description')}
            </p>
          </div>
        )}
      </div>

      {/* Disconnect Confirmation Modal */}
      <ConfirmationModal
        open={disconnectModal.open}
        onOpenChange={(open) => setDisconnectModal({ open, channel: open ? disconnectModal.channel : null })}
        title={t('settings_modal_disconnect_title')}
        description={t('settings_modal_disconnect_description', { name: disconnectModal.channel?.platformUsername || PLATFORM_CONFIG[disconnectModal.channel?.platform || '']?.label || t('settings_modal_disconnect_this_channel') })}
        variant="warning"
        confirmText={t('settings_modal_disconnect_confirm')}
        cancelText={t('settings_modal_cancel')}
        onConfirm={confirmDisconnect}
        isLoading={disconnectMutation.isPending}
      />

      {/* Bulk Delete Confirmation Modal */}
      <ConfirmationModal
        open={bulkDeleteModal}
        onOpenChange={setBulkDeleteModal}
        title={t('settings_channel_bulk_delete_title')}
        description={t('settings_channel_bulk_delete_description', { count: selectedChannels.size })}
        variant="danger"
        confirmText={t('settings_channel_bulk_delete_confirm', { count: selectedChannels.size })}
        cancelText={t('settings_modal_cancel')}
        onConfirm={confirmBulkDelete}
        isLoading={isBulkDeleting}
      />

      {/* Rename Channel Modal */}
      <Modal
        open={renameModal.open}
        onOpenChange={(open) => {
          setRenameModal({ open, channel: open ? renameModal.channel : null });
          if (!open) setNewDisplayName('');
        }}
        title={t('settings_modal_rename_title')}
        size="sm"
        footer={
          <div className="flex justify-end gap-3">
            <button
              onClick={() => setRenameModal({ open: false, channel: null })}
              disabled={isRenaming}
              className="px-4 py-2 rounded-lg text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 disabled:opacity-50 transition-colors"
            >
              {t('settings_modal_cancel')}
            </button>
            <button
              onClick={confirmRename}
              disabled={isRenaming || !newDisplayName.trim()}
              className="px-4 py-2 rounded-lg text-sm font-medium text-primary-600-contrast bg-primary-600 hover:bg-primary-700 disabled:opacity-50 transition-colors flex items-center gap-2"
            >
              {isRenaming && <Icon name={ICONS.LOADER} size="sm" className="animate-spin" ariaHidden />}
              {t('settings_modal_save')}
            </button>
          </div>
        }
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            {t('settings_channel_rename_prompt', { platform: PLATFORM_CONFIG[renameModal.channel?.platform || '']?.label || '', type: 'channel' })}
          </p>
          <div>
            <label htmlFor="channel-name" className="block text-sm font-medium text-gray-700 mb-1">
              {t('settings_modal_rename_label')}
            </label>
            <input
              id="channel-name"
              type="text"
              value={newDisplayName}
              onChange={(e) => setNewDisplayName(e.target.value)}
              placeholder={t('settings_modal_rename_placeholder')}
              maxLength={100}
              className="w-full px-4 py-2.5 rounded-lg border border-gray-200 focus:border-gray-400 outline-none text-sm transition-colors"
              autoFocus
            />
            <p className="mt-1 text-xs text-gray-400">{t('settings_channel_char_count', { count: newDisplayName.length })}</p>
          </div>
        </div>
      </Modal>

      {/* Bluesky Connect Modal */}
      <Modal
        open={blueskyModal}
        onOpenChange={(open) => {
          setBlueskyModal(open);
          if (!open) {
            setBlueskyHandle('');
            setBlueskyAppPassword('');
            setShowBlueskyPassword(false);
          }
        }}
        title={t('settings_bluesky_modal_title')}
        size="sm"
        footer={
          <div className="flex justify-end gap-3">
            <button
              onClick={() => setBlueskyModal(false)}
              disabled={isConnectingBluesky}
              className="px-4 py-2 rounded-lg text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 disabled:opacity-50 transition-colors"
            >
              {t('settings_modal_cancel')}
            </button>
            <button
              onClick={handleBlueskyConnect}
              disabled={isConnectingBluesky || !blueskyHandle.trim() || !blueskyAppPassword.trim()}
              className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-[#0085FF] hover:bg-[#0066CC] disabled:opacity-50 transition-colors flex items-center gap-2"
            >
              {isConnectingBluesky && <Icon name={ICONS.LOADER} size="sm" className="animate-spin" ariaHidden />}
              {t('settings_channel_button_connect')}
            </button>
          </div>
        }
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            {t('settings_bluesky_modal_description')}
          </p>

          <div>
            <label htmlFor="bluesky-handle" className="block text-sm font-medium text-gray-700 mb-1">
              {t('settings_bluesky_handle_label')}
            </label>
            <input
              id="bluesky-handle"
              type="text"
              value={blueskyHandle}
              onChange={(e) => setBlueskyHandle(e.target.value)}
              placeholder={t('settings_bluesky_handle_placeholder')}
              className="w-full px-4 py-2.5 rounded-lg border border-gray-200 focus:border-[#0085FF] focus:ring-1 focus:ring-[#0085FF] outline-none text-sm"
              autoFocus
            />
          </div>

          <div>
            <label htmlFor="bluesky-password" className="block text-sm font-medium text-gray-700 mb-1">
              {t('settings_bluesky_password_label')}
            </label>
            <div className="relative">
              <input
                id="bluesky-password"
                type={showBlueskyPassword ? 'text' : 'password'}
                value={blueskyAppPassword}
                onChange={(e) => setBlueskyAppPassword(e.target.value)}
                placeholder={t('settings_bluesky_password_placeholder')}
                className="w-full px-4 py-2.5 pr-10 rounded-lg border border-gray-200 focus:border-[#0085FF] focus:ring-1 focus:ring-[#0085FF] outline-none text-sm"
              />
              <button
                type="button"
                onClick={() => setShowBlueskyPassword(!showBlueskyPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                aria-label={showBlueskyPassword ? t('settings_password_hide') : t('settings_password_show')}
              >
                <Icon name={showBlueskyPassword ? ICONS.EYE_SLASH : ICONS.EYE} size="sm" ariaHidden />
              </button>
            </div>
          </div>

          <div className="flex items-start gap-2 rounded-lg bg-soft-mint p-3">
            <Icon name={ICONS.INFO} size="sm" color="text-dark-teal" ariaHidden className="mt-0.5 flex-shrink-0" />
            <p className="text-xs text-dark-teal">
              {t('settings_bluesky_info_text')}{' '}
              <a
                href="https://bsky.app/settings/app-passwords"
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium underline hover:no-underline"
              >
                {t('settings_bluesky_info_link')}
              </a>
            </p>
          </div>
        </div>
      </Modal>

      {/* WordPress Connect Modal */}
      <WordPressConnectModal
        open={wordpressModal}
        onOpenChange={setWordpressModal}
        onConnected={(siteUrl, wpUsername) => {
          setNotification({
            type: 'success',
            message: t('wordpress_connect_success', {
              defaultValue: `Connected to ${siteUrl} as ${wpUsername}`,
              siteUrl,
              wpUsername,
            }),
          });
        }}
      />

      {/* Canva Connection Label Modal — shown immediately after OAuth redirect */}
      <Modal
        open={canvaLabelModal.open}
        onOpenChange={(open) => {
          if (!open) {
            setCanvaLabelModal({ open: false, channelId: null, prefill: '' });
            setCanvaLabelValue('');
            // Show success notification when closing (whether labeled or skipped)
            setNotification({ type: 'success', message: t('canva_connected_success', { defaultValue: 'Canva connected successfully!' }) });
          }
        }}
        title={t('canva_label_modal_title', { defaultValue: 'Name this Canva connection' })}
        size="sm"
        footer={
          <div className="flex justify-end gap-3">
            <button
              onClick={() => {
                setCanvaLabelModal({ open: false, channelId: null, prefill: '' });
                setCanvaLabelValue('');
                setNotification({ type: 'success', message: t('canva_connected_success', { defaultValue: 'Canva connected successfully!' }) });
              }}
              disabled={updateCanvaLabel.isPending}
              className="px-4 py-2 rounded-lg text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 disabled:opacity-50 transition-colors"
            >
              {t('canva_label_modal_skip', { defaultValue: 'Skip' })}
            </button>
            <button
              onClick={() => {
                if (updateCanvaLabel.isPending) return;
                const trimmed = canvaLabelValue.trim();
                if (!trimmed || !canvaLabelModal.channelId) return;
                updateCanvaLabel.mutate(
                  { channelId: canvaLabelModal.channelId, label: trimmed },
                  {
                    onSuccess: () => {
                      setCanvaLabelModal({ open: false, channelId: null, prefill: '' });
                      setCanvaLabelValue('');
                      setNotification({ type: 'success', message: t('canva_connected_success', { defaultValue: 'Canva connected successfully!' }) });
                    },
                  }
                );
              }}
              disabled={updateCanvaLabel.isPending || !canvaLabelValue.trim()}
              className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-[#00C4CC] hover:bg-[#00adb5] disabled:opacity-50 transition-colors flex items-center gap-2"
            >
              {updateCanvaLabel.isPending && (
                <Icon name={ICONS.LOADER} size="sm" className="animate-spin" ariaHidden />
              )}
              {t('canva_label_modal_save', { defaultValue: 'Save name' })}
            </button>
          </div>
        }
      >
        <div className="space-y-4">
          <div className="flex items-start gap-2 rounded-lg bg-soft-mint p-3">
            <Icon name={ICONS.INFO} size="sm" color="text-dark-teal" ariaHidden className="mt-0.5 flex-shrink-0" />
            <p className="text-xs text-dark-teal">
              {t('canva_label_modal_help', { defaultValue: 'Canva doesn\'t share team names with third-party apps. Give this connection a name you\'ll recognize — e.g. "o8 Design Team" or "Personal Canva".' })}
            </p>
          </div>
          <div>
            <label htmlFor="canva-label" className="block text-sm font-medium text-gray-700 mb-1">
              {t('canva_label_modal_field', { defaultValue: 'Connection name' })}
            </label>
            <input
              id="canva-label"
              type="text"
              value={canvaLabelValue}
              onChange={(e) => setCanvaLabelValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && canvaLabelValue.trim() && canvaLabelModal.channelId) {
                  e.preventDefault();
                  if (updateCanvaLabel.isPending) return;
                  updateCanvaLabel.mutate(
                    { channelId: canvaLabelModal.channelId, label: canvaLabelValue.trim() },
                    {
                      onSuccess: () => {
                        setCanvaLabelModal({ open: false, channelId: null, prefill: '' });
                        setCanvaLabelValue('');
                        setNotification({ type: 'success', message: t('canva_connected_success', { defaultValue: 'Canva connected successfully!' }) });
                      },
                    }
                  );
                }
              }}
              placeholder={t('canva_label_modal_placeholder', { defaultValue: 'e.g. o8 Design Team' })}
              maxLength={100}
              autoFocus
              className="w-full px-4 py-2.5 rounded-lg border border-gray-200 focus:border-gray-400 outline-none text-sm transition-colors"
            />
            <p className="mt-1 text-xs text-gray-400">{t('canva_label_char_count', { count: canvaLabelValue.length, defaultValue: '{{count}}/100 characters' })}</p>
          </div>
        </div>
      </Modal>

      {/* GDrive team picker — connect-time team-context fix (SPEC §2/§5.1) */}
      <GDriveTeamPickerModal
        open={gdriveTeamPickerOpen}
        onOpenChange={setGdriveTeamPickerOpen}
        onContinue={handleGDriveTeamPicked}
        isSubmitting={isConnectingGDrive}
      />

    </div>
  );
};
